import { type Href } from 'expo-router';
import { type ReactNode } from 'react';
import { Pressable, View } from 'react-native';

import { cn } from './cn';
import { Chevron } from './Chevron';
import { Linkable } from './Linkable';
import { Text } from './Text';

export interface ListRowProps {
  leading?: ReactNode;
  title: string;
  titleClassName?: string;
  /** Secondary line — string (muted caption) or a custom node. */
  subtitle?: string | ReactNode;
  trailing?: ReactNode;
  /** Show the default chevron when no `trailing` is given. */
  chevron?: boolean;
  /** Navigation target — renders a real `<a href>` on web (cmd/middle-click, etc.). */
  href?: Href;
  onPress?: () => void;
  /** Mute the row and block presses (e.g. a template that isn't runnable yet). */
  disabled?: boolean;
  className?: string;
}

/** Generic pressable row — templates, history, settings, sheet lists. */
export function ListRow({
  leading,
  title,
  titleClassName,
  subtitle,
  trailing,
  chevron = true,
  href,
  onPress,
  disabled,
  className,
}: ListRowProps) {
  return (
    <Linkable href={href} disabled={disabled}>
      <Pressable
        onPress={onPress}
        disabled={disabled}
        className={cn(
          'flex-row items-center gap-3 px-4 py-3.5',
          disabled ? 'opacity-40' : 'active:opacity-70',
          className,
        )}
      >
        {leading}
        <View className="flex-1">
          <Text variant="bodySemibold" numberOfLines={1} className={titleClassName}>
            {title}
          </Text>
          {typeof subtitle === 'string' ? (
            <Text variant="caption" numberOfLines={1} className="mt-0.5">
              {subtitle}
            </Text>
          ) : (
            subtitle
          )}
        </View>
        {trailing ?? (chevron && !disabled ? <Chevron /> : null)}
      </Pressable>
    </Linkable>
  );
}
